/**
 * Editor для ref.pos_workstations (Порція Роздріб.5b).
 */
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { orgs } from '@/api/catalogs';
import {
  retailPosWorkstations,
  type WorkstationType,
} from '@/api/retailPosWorkstations';
import { retailSalesChannels } from '@/api/retailSalesChannels';
import { retailPosProviders } from '@/api/retailPosProviders';
import { retailPriceGroups } from '@/api/retailPriceGroups';
import { refs } from '@/api/refs';
import { usePermissionsByOrg } from '@/hooks/usePermissionsByOrg';
import { useNextRefCode } from '@/hooks/useNextRefCode';
import RequirePermission from '@/auth/RequirePermission';
import { validateRequired } from '@/utils/validateRequired';
import { extractApiError } from '@/utils/extractApiError';
import RequiredLabel from '@/components/RequiredLabel';

const PERMISSION = 'retail.workstation.manage';

interface FormState {
  organization_id: string;
  code: string;
  name: string;
  description: string;
  workstation_type: WorkstationType;
  sales_channel_id: string;
  pos_provider_id: string;
  warehouse_id: string;
  cash_register_id: string;
  default_price_group_id: string;
  fiscal_registration_number: string;
  is_active: boolean;
  comment: string;
}

const EMPTY: FormState = {
  organization_id: '',
  code: '',
  name: '',
  description: '',
  workstation_type: 'desktop',
  sales_channel_id: '',
  pos_provider_id: '',
  warehouse_id: '',
  cash_register_id: '',
  default_price_group_id: '',
  fiscal_registration_number: '',
  is_active: true,
  comment: '',
};

export default function PosWorkstationEditor() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { id } = useParams();
  const isNew = !id || id === 'new';

  const orgList = useQuery({ queryKey: ['orgs'], queryFn: () => orgs.list({ limit: 200 }) });
  const { availableOrgs } = usePermissionsByOrg(PERMISSION);

  const [form, setForm] = useState<FormState>(EMPTY);
  const [error, setError] = useState<string | null>(null);

  const warehousesQ = useQuery({
    queryKey: ['warehouses-for-pos-ws', form.organization_id],
    queryFn: () => refs.warehouses(form.organization_id),
    enabled: !!form.organization_id,
  });
  const cashRegistersQ = useQuery({
    queryKey: ['cash-registers-for-pos-ws', form.organization_id],
    queryFn: () => refs.cashRegisters(form.organization_id),
    enabled: !!form.organization_id,
  });
  const channelsQ = useQuery({
    queryKey: ['retail-sales-channels-for-ws', form.organization_id],
    queryFn: () => retailSalesChannels.list({ organization_id: form.organization_id, limit: 500 }),
    enabled: !!form.organization_id,
  });
  const providersQ = useQuery({
    queryKey: ['retail-pos-providers-for-ws', form.organization_id],
    queryFn: () => retailPosProviders.list({ organization_id: form.organization_id, limit: 500 }),
    enabled: !!form.organization_id,
  });
  const pgQ = useQuery({
    queryKey: ['retail-price-groups-for-ws', form.organization_id],
    queryFn: () => retailPriceGroups.list({ organization_id: form.organization_id, limit: 500 }),
    enabled: !!form.organization_id,
  });

  const docQuery = useQuery({
    queryKey: ['retail-pos-workstation', id],
    queryFn: () => retailPosWorkstations.get(id!),
    enabled: !isNew,
  });

  useEffect(() => {
    if (isNew && availableOrgs.length > 0 && !form.organization_id) {
      setForm((f) => ({ ...f, organization_id: availableOrgs[0]!.id }));
    }
  }, [isNew, availableOrgs, form.organization_id]);

  // Авто-пропозиція коду POS-NN
  const nextCode = useNextRefCode({
    queryKey: ['retail-pos-workstations', form.organization_id, 'for-next-code'],
    queryFn: () =>
      retailPosWorkstations.list({ organization_id: form.organization_id, limit: 500 }),
    extractCodes: (data) => data.items.map((i) => i.code),
    prefix: 'POS',
    enabled: isNew && !!form.organization_id,
  });
  useEffect(() => {
    if (isNew && nextCode.suggested && !form.code) {
      setForm((f) => ({ ...f, code: nextCode.suggested }));
    }
  }, [isNew, nextCode.suggested, form.code]);

  useEffect(() => {
    if (!isNew && docQuery.data) {
      const d = docQuery.data;
      setForm({
        organization_id: d.organization_id,
        code: d.code,
        name: d.name,
        description: d.description ?? '',
        workstation_type: d.workstation_type,
        sales_channel_id: d.sales_channel_id ?? '',
        pos_provider_id: d.pos_provider_id ?? '',
        warehouse_id: d.warehouse_id,
        cash_register_id: d.cash_register_id ?? '',
        default_price_group_id: d.default_price_group_id ?? '',
        fiscal_registration_number: d.fiscal_registration_number ?? '',
        is_active: d.is_active,
        comment: d.comment ?? '',
      });
    }
  }, [isNew, docQuery.data]);

  const payload = () => ({
    code: form.code,
    name: form.name,
    description: form.description || null,
    workstation_type: form.workstation_type,
    sales_channel_id: form.sales_channel_id || null,
    pos_provider_id: form.pos_provider_id || null,
    warehouse_id: form.warehouse_id,
    cash_register_id: form.cash_register_id || null,
    default_price_group_id: form.default_price_group_id || null,
    fiscal_registration_number: form.fiscal_registration_number || null,
    is_active: form.is_active,
    comment: form.comment || null,
  });

  const create = useMutation({
    mutationFn: () =>
      retailPosWorkstations.create({
        organization_id: form.organization_id,
        ...payload(),
      }),
    onSuccess: (doc) => {
      qc.invalidateQueries({ queryKey: ['retail-pos-workstations'] });
      navigate(`/retail/pos-workstations/${doc.id}`);
    },
    onError: (e) => setError(extractApiError(e)),
  });

  const update = useMutation({
    mutationFn: () => retailPosWorkstations.update(id!, payload()),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['retail-pos-workstations'] });
      qc.invalidateQueries({ queryKey: ['retail-pos-workstation', id] });
    },
    onError: (e) => setError(extractApiError(e)),
  });

  const remove = useMutation({
    mutationFn: () => retailPosWorkstations.remove(id!),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['retail-pos-workstations'] });
      navigate('/retail/pos-workstations');
    },
    onError: (e) => setError(extractApiError(e)),
  });

  const handleSave = () => {
    setError(null);
    const err = validateRequired(
      {
        organization_id: form.organization_id,
        code: form.code,
        name: form.name,
        warehouse_id: form.warehouse_id,
      },
      {
        organization_id: 'Юрособа',
        code: 'Код',
        name: 'Назва',
        warehouse_id: 'Склад',
      },
    );
    if (err) { setError(err); return; }
    if (isNew) create.mutate();
    else update.mutate();
  };

  return (
    <div className="card">
      <div className="toolbar">
        <h2 style={{ margin: 0 }}>
          {isNew ? 'Нова POS-станція' : `POS-станція: ${form.code} — ${form.name}`}
        </h2>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <button onClick={() => navigate('/retail/pos-workstations')}>Скасувати</button>
          <RequirePermission code={PERMISSION}>
            <button
              className="primary"
              onClick={handleSave}
              disabled={create.isPending || update.isPending}
            >
              Зберегти
            </button>
          </RequirePermission>
          {!isNew && (
            <RequirePermission code={PERMISSION}>
              <button
                className="danger"
                onClick={() => {
                  if (window.confirm('Видалити станцію?')) remove.mutate();
                }}
              >
                Видалити
              </button>
            </RequirePermission>
          )}
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
        <div className="field">
          <RequiredLabel>Організація</RequiredLabel>
          {isNew ? (
            <select
              value={form.organization_id}
              onChange={(e) => setForm({ ...form, organization_id: e.target.value })}
            >
              <option value="">—</option>
              {availableOrgs.map((o) => (
                <option key={o.id} value={o.id}>
                  {o.code} — {o.name}
                </option>
              ))}
            </select>
          ) : (
            <div>
              {orgList.data?.items.find((o) => o.id === form.organization_id)?.name ??
                form.organization_id}
            </div>
          )}
        </div>
        <div className="field">
          <RequiredLabel>Код</RequiredLabel>
          <input
            value={form.code}
            onChange={(e) => setForm({ ...form, code: e.target.value })}
            maxLength={30}
          />
        </div>
        <div className="field">
          <RequiredLabel>Назва</RequiredLabel>
          <input
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            maxLength={255}
          />
        </div>
        <div className="field">
          <label>Тип</label>
          <select
            value={form.workstation_type}
            onChange={(e) =>
              setForm({ ...form, workstation_type: e.target.value as WorkstationType })
            }
          >
            <option value="desktop">Стаціонарний</option>
            <option value="mobile">Мобільний</option>
            <option value="tablet">Планшет</option>
          </select>
        </div>
        <div className="field">
          <RequiredLabel>Склад</RequiredLabel>
          <select
            value={form.warehouse_id}
            onChange={(e) => setForm({ ...form, warehouse_id: e.target.value })}
          >
            <option value="">—</option>
            {warehousesQ.data?.map((w) => (
              <option key={w.id} value={w.id}>
                {w.code} — {w.name}
              </option>
            ))}
          </select>
        </div>
        <div className="field">
          <label>Каса</label>
          <select
            value={form.cash_register_id}
            onChange={(e) => setForm({ ...form, cash_register_id: e.target.value })}
          >
            <option value="">— (не призначено)</option>
            {cashRegistersQ.data?.map((c) => (
              <option key={c.id} value={c.id}>
                {c.code} — {c.name}
              </option>
            ))}
          </select>
        </div>
        <div className="field">
          <label>Канал продажу</label>
          <select
            value={form.sales_channel_id}
            onChange={(e) => setForm({ ...form, sales_channel_id: e.target.value })}
          >
            <option value="">—</option>
            {channelsQ.data?.items.map((c) => (
              <option key={c.id} value={c.id}>
                {c.code} — {c.name}
              </option>
            ))}
          </select>
        </div>
        <div className="field">
          <label>ПРРО-провайдер</label>
          <select
            value={form.pos_provider_id}
            onChange={(e) => setForm({ ...form, pos_provider_id: e.target.value })}
          >
            <option value="">—</option>
            {providersQ.data?.items.map((p) => (
              <option key={p.id} value={p.id}>
                {p.code} — {p.name}
              </option>
            ))}
          </select>
        </div>
        <div className="field">
          <label>Цінова група за замовч.</label>
          <select
            value={form.default_price_group_id}
            onChange={(e) =>
              setForm({ ...form, default_price_group_id: e.target.value })
            }
          >
            <option value="">—</option>
            {pgQ.data?.items.map((g) => (
              <option key={g.id} value={g.id}>
                {g.code} — {g.name}
              </option>
            ))}
          </select>
        </div>
        <div className="field">
          <label>Реєстраційний № фіскального апарату</label>
          <input
            value={form.fiscal_registration_number}
            onChange={(e) =>
              setForm({ ...form, fiscal_registration_number: e.target.value })
            }
            maxLength={50}
            placeholder="з реєстрації ПРРО у ДПС"
          />
        </div>
        <div className="field">
          <label>
            <input
              type="checkbox"
              checked={form.is_active}
              onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
            />
            {' '}Активна
          </label>
        </div>
        <div className="field" style={{ gridColumn: 'span 2' }}>
          <label>Опис</label>
          <textarea
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
            rows={2}
          />
        </div>
        <div className="field" style={{ gridColumn: 'span 2' }}>
          <label>Коментар</label>
          <textarea
            value={form.comment}
            onChange={(e) => setForm({ ...form, comment: e.target.value })}
            rows={2}
          />
        </div>
      </div>

      {error && (
        <div className="error" style={{ marginTop: '1rem', color: '#b91c1c' }}>
          {error}
        </div>
      )}
    </div>
  );
}
